import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { trackPageView } from "./lib/analytics";
import './App.css';
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CreditsProvider } from "./contexts/CreditsContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import DashboardLayout from "./components/Dashboard/DashboardLayout";
import CookieBanner from "./components/CookieConsent/CookieBanner";
import ChatWidget from "./components/Chat/ChatWidget";
import { initSounds } from './lib/sounds';
import ServicePage from "./components/Services/ServicePage";
import Branding from "./components/Branding-And-Hosting/Branding";
import * as Sentry from "@sentry/react";

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'production',
    tracesSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// ── Global frontend error capture ────────────────────────────────────────────
const APP_VERSION = require('../package.json').version;
let _errorCaptureInstalled = false;
function installGlobalErrorCapture() {
  if (_errorCaptureInstalled || typeof window === 'undefined') return;
  _errorCaptureInstalled = true;
  const postError = (payload) => {
    try {
      fetch('/api/user/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...payload,
          user_agent: navigator.userAgent,
          app_version: APP_VERSION,
        }),
      }).catch(() => {});
    } catch (_) {}
  };
  window.onerror = (message, source, lineno, colno, error) => {
    postError({
      message: String(message),
      stack: error?.stack || `${source}:${lineno}:${colno}`,
      page_url: window.location.pathname,
      component: 'window.onerror',
      severity: /TypeError|ReferenceError|SyntaxError/.test(String(message)) ? 'critical' : 'error',
    });
    return false;
  };
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev.reason;
    const msg = reason?.message || String(reason) || 'Unhandled Promise Rejection';
    // Don't double-capture lock errors (already silenced in index.js)
    if (msg.includes('Lock broken') || msg.includes('steal')) return;
    postError({
      message: msg,
      stack: reason?.stack || '',
      page_url: window.location.pathname,
      component: 'unhandledrejection',
      severity: /TypeError|ReferenceError/.test(msg) ? 'critical' : 'warning',
    });
  });
}
installGlobalErrorCapture();

const Home = lazy(() => import("./components/home/Home"));
const ShowcaseRepPreview         = lazy(() => import("./pages/ShowcaseRepPreview"));
const ShowcaseClientPreview      = lazy(() => import("./pages/ShowcaseClientPreview"));
const ShowcaseContractorPreview  = lazy(() => import("./pages/ShowcaseContractorPreview"));
const ClientServicesPage = lazy(() => import("./components/Services/ClientServicesPage"));
const ServicesLanding = lazy(() => import("./components/Services/ServicesLanding"));
const QuinnWidget = lazy(() => import("./components/Quinn/QuinnWidget"));
const AboutUs = lazy(() => import("./components/AboutUs/About"));
const Portfolio = lazy(() => import("./components/Portfolio/Portfolio"));
const ContactUs = lazy(() => import("./components/ContactUs/ContactUs"));
const Faqs = lazy(() => import("./components/Faqs/Index"));
const Login = lazy(() => import("./components/Auth/Login"));
const AdminVerify = lazy(() => import("./components/Auth/AdminVerify"));
const Signup = lazy(() => import("./components/Auth/Signup"));
const InviteSignup = lazy(() => import("./components/Auth/InviteSignup"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const SalesRepDashboard = lazy(() => import("./components/Dashboard/SalesRep/SalesRepDashboard"));
const ReferralLinks = lazy(() => import("./components/Dashboard/SalesRep/ReferralLinks"));
const MyClients = lazy(() => import("./components/Dashboard/SalesRep/MyClients"));
const SalesCommissions = lazy(() => import("./components/Dashboard/SalesRep/Commissions"));
const RepPayoutDashboard = lazy(() => import("./components/Dashboard/SalesRep/RepPayoutDashboard"));
const SalesRequests = lazy(() => import("./components/Dashboard/SalesRep/SalesRequests"));
const SalesQuotes = lazy(() => import("./components/Dashboard/SalesRep/SalesQuotes"));
const CustomOffers = lazy(() => import("./components/Dashboard/SalesRep/CustomOffers"));
const OfferPage = lazy(() => import("./components/Offers/OfferPage"));

const ClientDashboard = lazy(() => import("./components/Dashboard/Client/ClientDashboard"));
const RequestService = lazy(() => import("./components/Dashboard/Client/RequestService"));
const MyServices = lazy(() => import("./components/Dashboard/Client/MyServices"));
const MySalesRep = lazy(() => import("./components/Dashboard/Client/MySalesRep"));

const AdminDashboard = lazy(() => import("./components/Dashboard/Admin/AdminDashboard"));
const LiveChats = lazy(() => import("./components/Dashboard/Admin/LiveChats"));
const SalesAgentDashboard = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentDashboard"));
const SalesAgentCalls = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentCalls"));
const SalesAgentSMS = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentSMS"));
const SalesAgentCampaigns = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentCampaigns"));
const SalesAgentVoiceSettings = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentVoiceSettings"));
const SalesAgentLiveCall = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentLiveCall"));
const SalesAgentIntelligence = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentIntelligence"));
const SalesAgentCalendar = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SalesAgentCalendar"));
const SMSCalendarPanel = lazy(() => import("./components/Dashboard/Admin/SalesAgent/SMSCalendarPanel"));
const DataApprovals = lazy(() => import("./components/Dashboard/Admin/DataApprovals"));
const LeadVerifications = lazy(() => import("./components/Dashboard/Admin/LeadVerifications"));
const ContactReports = lazy(() => import("./components/Dashboard/Admin/ContactReports"));
const ProspectFinder = lazy(() => import("./components/Dashboard/Admin/ProspectFinder"));
const ApolloFinder   = lazy(() => import("./components/Dashboard/Admin/ApolloFinder"));
const HunterFinder   = lazy(() => import("./components/Dashboard/Admin/HunterFinder"));
const RegistryFinder = lazy(() => import("./components/Dashboard/Admin/RegistryFinder"));
const AllSalesReps = lazy(() => import("./components/Dashboard/Admin/AllSalesReps"));
const AdminThemesPage = lazy(() => import("./components/Dashboard/Admin/AdminThemesPage"));
const AllClients = lazy(() => import("./components/Dashboard/Admin/AllClients"));
const AllServices = lazy(() => import("./components/Dashboard/Admin/AllServices"));
const ServiceRequestDetail = lazy(() => import("./components/Dashboard/Admin/ServiceRequestDetail"));
const ClientServiceRequestDetail = lazy(() => import("./components/Dashboard/Client/ServiceRequestDetail"));
const InviteCenter = lazy(() => import("./components/Dashboard/Admin/InviteCenter"));
const JobsManager = lazy(() => import("./components/Dashboard/Admin/JobsManager"));
const AdminJobReview = lazy(() => import("./components/Dashboard/Admin/AdminJobReview"));
const UserManager = lazy(() => import("./components/Dashboard/Admin/UserManager"));
const AdminUserDetail = lazy(() => import("./components/Dashboard/Admin/AdminUserDetail"));
const PayoutQueue = lazy(() => import("./components/Dashboard/Admin/PayoutQueue"));
const AdminContractorPayoutQueue = lazy(() => import("./components/Dashboard/Admin/AdminContractorPayoutQueue"));
const BugReports = lazy(() => import("./components/Dashboard/Admin/BugReports"));
const AdminErrorDashboard = lazy(() => import("./components/Dashboard/Admin/AdminErrorDashboard"));
const PromoCodesPanel = lazy(() => import("./components/Dashboard/Admin/PromoCodesPanel"));
const FinderCommissions = lazy(() => import("./components/Dashboard/Admin/FinderCommissions"));
const MonthlyReport = lazy(() => import("./components/Dashboard/Reports/MonthlyReport"));

const ContractorDashboard = lazy(() => import("./components/Dashboard/Contractor/ContractorDashboard"));
const AssignedJobs = lazy(() => import("./components/Dashboard/Contractor/AssignedJobs"));
const ContractorJobsList = lazy(() => import("./components/Dashboard/Contractor/ContractorJobsList"));
const ContractorJobDetail = lazy(() => import("./components/Dashboard/Contractor/ContractorJobDetail"));
const ContractorPricing = lazy(() => import("./components/Dashboard/Contractor/ContractorPricing"));
const MySubmissions = lazy(() => import("./components/Dashboard/Contractor/MySubmissions"));
const MyPayouts = lazy(() => import("./components/Dashboard/Contractor/MyPayouts"));
const ContractorPayouts = lazy(() => import("./components/Dashboard/Contractor/ContractorPayouts"));
const ContractorProjects = lazy(() => import("./components/Dashboard/Contractor/ContractorProjects"));
const ContractorAdminDashboard = lazy(() => import("./components/Dashboard/ContractorAdmin/ContractorAdminDashboard"));
const ContractorOnboarding = lazy(() => import("./components/Auth/ContractorOnboarding"));
const ContractorApplications = lazy(() => import("./components/Dashboard/Admin/ContractorApplications"));
const PhoneNumbers           = lazy(() => import("./components/Dashboard/Admin/PhoneNumbers"));
const CommissionApprovals    = lazy(() => import("./components/Dashboard/Admin/CommissionApprovals"));
const AdminPayoutDashboard   = lazy(() => import("./components/Dashboard/Admin/AdminPayoutDashboard"));
const SecurityDashboard      = lazy(() => import("./components/Dashboard/Admin/SecurityDashboard"));

const MyJobs = lazy(() => import("./components/Dashboard/Client/MyJobs"));
const ClientProjects = lazy(() => import("./components/Dashboard/Client/ClientProjects"));
const Approvals = lazy(() => import("./components/Dashboard/Client/Approvals"));
const ClientFiles = lazy(() => import("./components/Dashboard/Client/ClientFiles"));
const ClientBilling = lazy(() => import("./components/Dashboard/Client/ClientBilling"));
const ClientPerks = lazy(() => import("./components/Dashboard/Client/ClientPerks"));
const ClientPlans = lazy(() => import("./components/Dashboard/Client/ClientPlans"));
const ClientReferral = lazy(() => import("./components/Dashboard/Client/ClientReferral"));
const ServiceCatalogue = lazy(() => import("./components/Dashboard/Client/ServiceCatalogue"));

const LeadPipeline = lazy(() => import("./components/Dashboard/SalesRep/LeadPipeline"));
const AIProspects = lazy(() => import("./components/Dashboard/SalesRep/AIProspects"));
const LearningCentre = lazy(() => import("./components/Dashboard/SalesRep/LearningCentre"));
const PricingPage = lazy(() => import("./components/Credits/PricingPage"));
const CreditManager = lazy(() => import("./components/Dashboard/Admin/CreditManager"));
const AdminRepSubscriptions = lazy(() => import("./components/Dashboard/Admin/AdminRepSubscriptions"));
const JobsList = lazy(() => import("./components/Dashboard/Jobs/JobsList"));
const JobDetail = lazy(() => import("./components/Dashboard/Jobs/JobDetail"));
const CreateJob = lazy(() => import("./components/Dashboard/Jobs/CreateJob"));
const Inbox = lazy(() => import("./components/Dashboard/Messages/Inbox"));
const FileVault = lazy(() => import("./components/Dashboard/Files/FileVault"));

const Profile = lazy(() => import("./components/Dashboard/Profile/Profile"));
const NotificationsPage = lazy(() => import("./components/Dashboard/NotificationsPage"));

const FunnelPayouts = lazy(() => import("./components/Onboarding/FunnelPayouts"));
const FunnelPlan = lazy(() => import("./components/Onboarding/FunnelPlan"));
const FunnelSuccess = lazy(() => import("./components/Onboarding/FunnelSuccess"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const FunnelAnalytics = lazy(() => import("./components/Dashboard/Admin/FunnelAnalytics"));
const SiteAnalytics  = lazy(() => import("./components/Dashboard/Admin/SiteAnalytics"));
const AdminProfitability = lazy(() => import("./components/Dashboard/Admin/AdminProfitability"));
const ClientLTV         = lazy(() => import("./components/Dashboard/Admin/ClientLTV"));
const ChurnPrediction   = lazy(() => import("./components/Dashboard/Admin/ChurnPrediction"));
const AdminCaseStudies  = lazy(() => import("./components/Dashboard/Admin/AdminCaseStudies"));
const AdminGoogleReviews = lazy(() => import("./components/Dashboard/Admin/AdminGoogleReviews"));
const AdminContracts    = lazy(() => import("./components/Dashboard/Admin/AdminContracts"));
const AdminNdas         = lazy(() => import("./components/Dashboard/Admin/AdminNdas"));
const AdminFeedback     = lazy(() => import("./components/Dashboard/Admin/AdminFeedback"));
const ClientContractSign = lazy(() => import("./components/Dashboard/Client/ClientContractSign"));
const ClientNdaSign     = lazy(() => import("./components/Dashboard/Client/ClientNdaSign"));
const CaseStudies       = lazy(() => import("./components/CaseStudies/CaseStudies"));
const AdminProjects = lazy(() => import("./components/Dashboard/Admin/AdminProjects"));
const PaymentTests = lazy(() => import("./components/Dashboard/Admin/PaymentTests"));
const SystemHealth     = lazy(() => import("./components/Dashboard/Admin/SystemHealth"));
const FeatureControls  = lazy(() => import("./components/Dashboard/Admin/FeatureControls"));
const WebhookErrors = lazy(() => import("./components/Dashboard/Admin/WebhookErrors"));

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen bg-gray-900">
    <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const IS_SHOWCASE = typeof window !== 'undefined' && window.location.pathname.startsWith('/showcase/');

function SoundSetup() {
  useEffect(() => { if (!IS_SHOWCASE) initSounds(); }, []);
  return null;
}

// ─── UA parsing helpers (no library) ─────────────────────────────────────────
function _parseBrowserInfo() {
  const ua = navigator.userAgent || '';
  let browser = 'Other', bv = '';
  if (/Edg\//.test(ua))                              { browser = 'Edge';    bv = (ua.match(/Edg\/([\d.]+)/)     || [])[1] || ''; }
  else if (/OPR\//.test(ua))                         { browser = 'Opera';   bv = (ua.match(/OPR\/([\d.]+)/)     || [])[1] || ''; }
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) { browser = 'Chrome';  bv = (ua.match(/Chrome\/([\d.]+)/)  || [])[1] || ''; }
  else if (/Firefox\//.test(ua))                     { browser = 'Firefox'; bv = (ua.match(/Firefox\/([\d.]+)/)  || [])[1] || ''; }
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua))   { browser = 'Safari';  bv = (ua.match(/Version\/([\d.]+)/) || [])[1] || ''; }
  else if (/MSIE|Trident/.test(ua))                  { browser = 'IE'; }
  let os = 'Other';
  if (/Windows/.test(ua))          os = 'Windows';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua))     os = 'Android';
  else if (/Mac OS X/.test(ua))    os = 'macOS';
  else if (/CrOS/.test(ua))        os = 'ChromeOS';
  else if (/Linux/.test(ua))       os = 'Linux';
  let device = 'Desktop';
  if (/iPad|Tablet/.test(ua))                              device = 'Tablet';
  else if (/Mobile|Android.*Mobile|iPhone|iPod/.test(ua)) device = 'Mobile';
  return { browser, browser_version: bv, os, device_type: device };
}
function _getOrCreateId(key, expireMs) {
  try {
    const raw = localStorage.getItem(key);
    const stored = raw ? JSON.parse(raw) : null;
    if (stored && stored.id) {
      if (!expireMs) return stored.id;
      if (Date.now() - stored.ts < expireMs) {
        localStorage.setItem(key, JSON.stringify({ id: stored.id, ts: Date.now() }));
        return stored.id;
      }
    }
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, JSON.stringify({ id, ts: Date.now() }));
    return id;
  } catch { return Math.random().toString(36).slice(2); }
}

function PageTracker() {
  const location  = useLocation();
  const pvIdRef   = useRef(null);
  const enterRef  = useRef(null);

  useEffect(() => {
    trackPageView(location.pathname + location.search);

    // Send duration for the previous page before navigating away
    if (pvIdRef.current && enterRef.current) {
      const dur = Math.round((Date.now() - enterRef.current) / 1000);
      const id  = pvIdRef.current;
      if (dur > 0) {
        fetch('/api/tracker/duration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, duration_seconds: dur }),
        }).catch(() => {});
      }
    }

    const visitorId  = _getOrCreateId('qg_visitor_id', null);
    const sessionId  = _getOrCreateId('qg_session_id', 30 * 60 * 1000);
    const isNew      = !localStorage.getItem('qg_has_visited');
    if (isNew) { try { localStorage.setItem('qg_has_visited', '1'); } catch {} }
    const utmParams  = (() => {
      try {
        const sp = new URLSearchParams(window.location.search);
        return { utm_source: sp.get('utm_source'), utm_medium: sp.get('utm_medium'), utm_campaign: sp.get('utm_campaign') };
      } catch { return {}; }
    })();

    enterRef.current = Date.now();
    pvIdRef.current  = null;

    fetch('/api/tracker/pageview', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id:  sessionId,
        visitor_id:  visitorId,
        page_url:    location.pathname,
        page_title:  document.title,
        referrer:    document.referrer || null,
        ...utmParams,
        ..._parseBrowserInfo(),
        screen_width:  window.screen?.width  || null,
        screen_height: window.screen?.height || null,
        language:      navigator.language    || null,
        is_new_visitor: isNew,
      }),
    })
      .then(r => r.json())
      .then(d => { if (d && d.id) pvIdRef.current = d.id; })
      .catch(() => {});
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Send final duration on page unload
  useEffect(() => {
    const sendFinal = () => {
      if (!pvIdRef.current || !enterRef.current) return;
      const dur = Math.round((Date.now() - enterRef.current) / 1000);
      if (dur <= 0) return;
      try {
        navigator.sendBeacon('/api/tracker/duration',
          new Blob([JSON.stringify({ id: pvIdRef.current, duration_seconds: dur })], { type: 'application/json' })
        );
      } catch {}
    };
    const onVis = () => { if (document.visibilityState === 'hidden') sendFinal(); };
    window.addEventListener('beforeunload', sendFinal);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('beforeunload', sendFinal);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return null;
}

function DashboardRedirect() {
  const { profile, loading } = useAuth();
  const [fallbackTimedOut, setFallbackTimedOut] = useState(false);

  useEffect(() => {
    if (!loading && (!profile || profile._fallback)) {
      const t = setTimeout(() => setFallbackTimedOut(true), 7000);
      return () => clearTimeout(t);
    } else {
      setFallbackTimedOut(false);
    }
  }, [loading, profile]);

  if (loading) return <LoadingSpinner />;

  // Never assume a role while profile is still resolving
  if (!profile && !fallbackTimedOut) return <LoadingSpinner />;
  if (profile?._fallback && !fallbackTimedOut) return <LoadingSpinner />;

  // If profile is still unknown after timeout (or still a fallback), send to login so they can re-auth
  // Never route based on a fallback 'client' role — that would send a sales_rep to the client dashboard
  if (!profile || !profile.role || profile._fallback) return <Navigate to="/login" replace />;

  if (profile.role === 'sales_rep') {
    const step = profile.onboarding_step;
    if (!step || step === 'signup') return <Navigate to="/onboarding/payouts" replace />;
    if (step === 'connect') return <Navigate to="/onboarding/plan" replace />;
    if (step === 'plan') return <Navigate to="/onboarding/success" replace />;
    return <Navigate to="/dashboard/sales" replace />;
  }

  switch (profile.role) {
    case 'admin':            return <Navigate to="/dashboard/admin"            replace />;
    case 'contractor':       return <Navigate to="/dashboard/contractor"       replace />;
    case 'contractor_admin': return <Navigate to="/dashboard/contractor-admin" replace />;
    case 'client':           return <Navigate to="/dashboard/client"           replace />;
    default:
      // Unknown role — send to login; avoids silently putting someone in the wrong dashboard
      return <Navigate to="/login" replace />;
  }
}

function DashboardWrapper() {
  const { user, profile, signOut } = useAuth();
  return <DashboardLayout user={user} profile={profile} onSignOut={signOut} />;
}

function QuinnWidgetWrapper() {
  const location = useLocation();
  if (!location.pathname.startsWith('/dashboard/client')) return null;
  return (
    <React.Suspense fallback={null}>
      <QuinnWidget />
    </React.Suspense>
  );
}

function ChatWidgetWrapper() {
  const location = useLocation();
  if (IS_SHOWCASE) return null;
  if (location.pathname.startsWith('/dashboard')) return null;
  return <ChatWidget />;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <Router>
      <SoundSetup />
      <PageTracker />
      <AuthProvider>
        <CreditsProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/services" element={<ClientServicesPage />} />
            <Route path="/services-overview" element={<ServicesLanding />} />
            <Route path="/Branding" element={<Branding />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/case-studies" element={<Suspense fallback={<LoadingSpinner />}><CaseStudies /></Suspense>} />
            <Route path="/case-studies/:slug" element={<Suspense fallback={<LoadingSpinner />}><CaseStudies /></Suspense>} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/faqs" element={<Faqs />} />
            <Route path="/faq" element={<Faqs />} />

            <Route path="/login" element={<Login />} />
            <Route path="/admin-verify" element={<AdminVerify />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/invite" element={<InviteSignup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/offer/:code" element={<OfferPage />} />
            <Route path="/showcase/rep"        element={<ShowcaseRepPreview />} />
            <Route path="/showcase/client"     element={<ShowcaseClientPreview />} />
            <Route path="/showcase/contractor" element={<ShowcaseContractorPreview />} />

            <Route path="/services/web/:servicePath" element={<ServicePage />} />
            <Route path="/services/hosting/:servicePath" element={<ServicePage />} />
            <Route path="/services/digital-marketing/:servicePath" element={<ServicePage />} />
            <Route path="/services/software/:servicePath" element={<ServicePage />} />
            <Route path="/services/artificial-intelligence/:servicePath" element={<ServicePage />} />
            <Route path="/services/branding-and-hosting" element={<Branding />} />
            <Route path="/services/:category/:servicePath" element={<ServicePage />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contractor-onboarding"
              element={
                <ProtectedRoute allowedRoles={['contractor']}>
                  <Suspense fallback={<LoadingSpinner />}><ContractorOnboarding /></Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="/onboarding/payouts"
              element={
                <ProtectedRoute allowedRoles={['sales_rep']}>
                  <Suspense fallback={<LoadingSpinner />}><FunnelPayouts /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/plan"
              element={
                <ProtectedRoute allowedRoles={['sales_rep']}>
                  <Suspense fallback={<LoadingSpinner />}><FunnelPlan /></Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/success"
              element={
                <ProtectedRoute allowedRoles={['sales_rep']}>
                  <Suspense fallback={<LoadingSpinner />}><FunnelSuccess /></Suspense>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/client"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <DashboardWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<ClientDashboard />} />
              <Route path="services" element={<MyServices />} />
              <Route path="services/:id" element={<ClientServiceRequestDetail />} />
              <Route path="jobs" element={<MyJobs />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="projects" element={<Suspense fallback={<LoadingSpinner />}><ClientProjects /></Suspense>} />
              <Route path="contract/:projectId" element={<Suspense fallback={<LoadingSpinner />}><ClientContractSign /></Suspense>} />
              <Route path="nda/:projectId" element={<Suspense fallback={<LoadingSpinner />}><ClientNdaSign /></Suspense>} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="messages" element={<Inbox />} />
              <Route path="files" element={<ClientFiles />} />
              <Route path="request" element={<RequestService />} />
              <Route path="requests" element={<RequestService />} />
              <Route path="services" element={<ServiceCatalogue />} />
              <Route path="reports" element={<MonthlyReport />} />
              <Route path="billing" element={<ClientBilling />} />
              <Route path="subscriptions" element={<Suspense fallback={<LoadingSpinner />}><ClientPerks /></Suspense>} />
              <Route path="plans" element={<ClientPlans />} />
              <Route path="referral" element={<Suspense fallback={<LoadingSpinner />}><ClientReferral /></Suspense>} />
              <Route path="sales-rep" element={<MySalesRep />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route
              path="/dashboard/sales"
              element={
                <ProtectedRoute allowedRoles={['sales_rep']}>
                  <DashboardWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<SalesRepDashboard />} />
              <Route path="pipeline" element={<LeadPipeline />} />
              <Route path="ai-prospects" element={<AIProspects />} />
              <Route path="clients" element={<MyClients />} />
              <Route path="requests" element={<SalesRequests />} />
              <Route path="requests/:id" element={<ServiceRequestDetail />} />
              <Route path="quotes" element={<SalesQuotes />} />
              <Route path="offers" element={<CustomOffers />} />
              <Route path="referrals" element={<ReferralLinks />} />
              <Route path="jobs" element={<JobsList />} />
              <Route path="jobs/create" element={<CreateJob />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="commissions" element={<SalesCommissions />} />
              <Route path="payout-dashboard" element={<Suspense fallback={<LoadingSpinner />}><RepPayoutDashboard /></Suspense>} />
              <Route path="learning" element={<LearningCentre />} />
              <Route path="messages" element={<Inbox />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="profile" element={<Profile />} />
              <Route path="notifications" element={<NotificationsPage role="sales_rep" />} />
            </Route>

            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DashboardWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<UserManager />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="invites" element={<InviteCenter />} />
              <Route path="jobs" element={<JobsManager />} />
              <Route path="jobs/create" element={<CreateJob />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="payouts" element={<PayoutQueue />} />
              <Route path="reports" element={<MonthlyReport />} />
              <Route path="sales-reps" element={<AllSalesReps />} />
              <Route path="clients" element={<AllClients />} />
              <Route path="services" element={<AllServices />} />
              <Route path="services/:id" element={<ServiceRequestDetail />} />
              <Route path="leads" element={<LeadPipeline />} />
              <Route path="commissions" element={<SalesCommissions />} />
              <Route path="ai-prospects" element={<AIProspects />} />
              <Route path="prospect-finder" element={<ProspectFinder />} />
              <Route path="apollo-finder"   element={<ApolloFinder />} />
              <Route path="hunter-finder"   element={<HunterFinder />} />
              <Route path="registry-finder" element={<RegistryFinder />} />
              <Route path="data-approvals" element={<DataApprovals />} />
              <Route path="lead-verifications" element={<LeadVerifications />} />
              <Route path="contact-reports" element={<ContactReports />} />
              <Route path="credit-manager" element={<CreditManager />} />
              <Route path="subscriptions" element={<AdminRepSubscriptions />} />
              <Route path="live-chats" element={<LiveChats />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="profile" element={<Profile />} />
              <Route path="notifications" element={<NotificationsPage role="admin" />} />
              <Route path="sales-agent" element={<SalesAgentDashboard />} />
              <Route path="sales-agent/calls" element={<SalesAgentCalls />} />
              <Route path="sales-agent/sms" element={<SalesAgentSMS />} />
              <Route path="sales-agent/campaigns" element={<SalesAgentCampaigns />} />
              <Route path="sales-agent/voice-settings" element={<SalesAgentVoiceSettings />} />
              <Route path="sales-agent/live-call" element={<SalesAgentLiveCall />} />
              <Route path="sales-agent/live-call/:lead_id" element={<SalesAgentLiveCall />} />
              <Route path="sales-agent/intelligence" element={<SalesAgentIntelligence />} />
              <Route path="sales-agent/calendar" element={<SalesAgentCalendar />} />
              <Route path="sales-agent/sms-calendar" element={<SMSCalendarPanel />} />
              <Route path="contractors" element={<ContractorDashboard />} />
              <Route path="contractors/jobs" element={<ContractorJobsList />} />
              <Route path="contractors/jobs/:id" element={<JobDetail />} />
              <Route path="contractors/subscriptions" element={<ContractorPricing />} />
              <Route path="contractor-admin" element={<ContractorAdminDashboard />} />
              <Route path="contractor-admin/contractors" element={<UserManager />} />
              <Route path="contractor-admin/contractors/:userId" element={<AdminUserDetail />} />
              <Route path="contractor-admin/jobs" element={<AdminJobReview />} />
              <Route path="contractor-admin/jobs/create" element={<CreateJob />} />
              <Route path="contractor-admin/jobs/:id" element={<JobDetail />} />
              <Route path="contractor-admin/payouts" element={<AdminContractorPayoutQueue />} />
              <Route path="bug-reports" element={<BugReports />} />
              <Route path="error-tracker" element={<Suspense fallback={<LoadingSpinner />}><AdminErrorDashboard /></Suspense>} />
              <Route path="promo-codes" element={<PromoCodesPanel />} />
              <Route path="funnel-analytics"  element={<Suspense fallback={<LoadingSpinner />}><FunnelAnalytics /></Suspense>} />
              <Route path="site-analytics"   element={<Suspense fallback={<LoadingSpinner />}><SiteAnalytics  /></Suspense>} />
              <Route path="profitability" element={<Suspense fallback={<LoadingSpinner />}><AdminProfitability /></Suspense>} />
              <Route path="client-ltv"   element={<Suspense fallback={<LoadingSpinner />}><ClientLTV /></Suspense>} />
              <Route path="churn"        element={<Suspense fallback={<LoadingSpinner />}><ChurnPrediction /></Suspense>} />
              <Route path="case-studies" element={<Suspense fallback={<LoadingSpinner />}><AdminCaseStudies /></Suspense>} />
              <Route path="google-reviews" element={<Suspense fallback={<LoadingSpinner />}><AdminGoogleReviews /></Suspense>} />
              <Route path="contracts" element={<Suspense fallback={<LoadingSpinner />}><AdminContracts /></Suspense>} />
              <Route path="ndas" element={<Suspense fallback={<LoadingSpinner />}><AdminNdas /></Suspense>} />
              <Route path="feedback" element={<Suspense fallback={<LoadingSpinner />}><AdminFeedback /></Suspense>} />
              <Route path="contractor-applications" element={<Suspense fallback={<LoadingSpinner />}><ContractorApplications /></Suspense>} />
              <Route path="phone-numbers"           element={<Suspense fallback={<LoadingSpinner />}><PhoneNumbers /></Suspense>} />
              <Route path="themes"                  element={<Suspense fallback={<LoadingSpinner />}><AdminThemesPage /></Suspense>} />
              <Route path="commission-approvals"   element={<Suspense fallback={<LoadingSpinner />}><CommissionApprovals /></Suspense>} />
              <Route path="commission-payouts"     element={<Suspense fallback={<LoadingSpinner />}><AdminPayoutDashboard /></Suspense>} />
              <Route path="security"              element={<Suspense fallback={<LoadingSpinner />}><SecurityDashboard /></Suspense>} />
              <Route path="projects" element={<Suspense fallback={<LoadingSpinner />}><AdminProjects /></Suspense>} />
              <Route path="payment-tests" element={<Suspense fallback={<LoadingSpinner />}><PaymentTests /></Suspense>} />
              <Route path="system-health"    element={<Suspense fallback={<LoadingSpinner />}><SystemHealth /></Suspense>} />
              <Route path="feature-controls" element={<Suspense fallback={<LoadingSpinner />}><FeatureControls /></Suspense>} />
              <Route path="webhook-errors" element={<Suspense fallback={<LoadingSpinner />}><WebhookErrors /></Suspense>} />
              <Route path="finder-commissions" element={<FinderCommissions />} />
            </Route>

            <Route
              path="/dashboard/contractor"
              element={
                <ProtectedRoute allowedRoles={['contractor']}>
                  <DashboardWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<ContractorDashboard />} />
              <Route path="jobs" element={<ContractorJobsList />} />
              <Route path="jobs/:id" element={<ContractorJobDetail />} />
              <Route path="submissions" element={<MySubmissions />} />
              <Route path="projects" element={<Suspense fallback={<LoadingSpinner />}><ContractorProjects /></Suspense>} />
              <Route path="payouts" element={<ContractorPayouts />} />
              <Route path="subscriptions" element={<ContractorPricing />} />
              <Route path="messages" element={<Inbox />} />
              <Route path="files" element={<FileVault />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route
              path="/dashboard/contractor-admin"
              element={
                <ProtectedRoute allowedRoles={['contractor_admin', 'admin']}>
                  <DashboardWrapper />
                </ProtectedRoute>
              }
            >
              <Route index element={<ContractorAdminDashboard />} />
              <Route path="contractors" element={<UserManager />} />
              <Route path="contractors/:userId" element={<AdminUserDetail />} />
              <Route path="jobs" element={<JobsManager />} />
              <Route path="jobs/create" element={<CreateJob />} />
              <Route path="jobs/:id" element={<JobDetail />} />
              <Route path="payouts" element={<AdminContractorPayoutQueue />} />
              <Route path="profile" element={<Profile />} />
            </Route>

            <Route path="/pricing" element={<PricingPage />} />

            <Route path="/about" element={<Navigate to="/about-us" replace />} />
            <Route path="/contact" element={<Navigate to="/contact-us" replace />} />

            <Route path="*" element={<div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
              <div className="text-center">
                <h1 className="text-6xl font-bold mb-6">404</h1>
                <p className="text-2xl mb-8">Page not found</p>
                <a href="/" className="px-6 py-3 bg-yellow-500 text-black font-medium rounded-full hover:bg-yellow-600 transition duration-300">
                  Return Home
                </a>
              </div>
            </div>} />
          </Routes>
        </Suspense>
        </CreditsProvider>
        <QuinnWidgetWrapper />
      </AuthProvider>
      <ChatWidgetWrapper />
      {!IS_SHOWCASE && <CookieBanner />}
    </Router>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
